import axios from 'axios';
import { cache } from '../utils/cache';
import { auth } from '../config/firebase';

const DEFAULT_DEPLOYED_BACKEND_URL = 'https://a-happ.vercel.app';

const normalizeBackendUrl = (value) => (value || '').trim().replace(/\/+$/, '');

const toApiBaseUrl = (backendUrl) => {
  const normalized = normalizeBackendUrl(backendUrl);
  return normalized ? `${normalized}/api` : '';
};

const PRIMARY_API_URL = toApiBaseUrl(process.env.REACT_APP_BACKEND_URL);
const FALLBACK_API_URL = toApiBaseUrl(
  process.env.REACT_APP_BACKEND_FALLBACK_URL || DEFAULT_DEPLOYED_BACKEND_URL
);

// Cache TTLs (in milliseconds)
const CACHE_TTL = {
  PHOTOS: 2 * 60 * 1000,       // 2 minutes
  STREAK: 30 * 1000,           // 30 seconds
  PROTOCOL: 5 * 60 * 1000,     // 5 minutes
  ADHERENCE: 5 * 60 * 1000,    // 5 minutes
  ROUTINES: 2 * 60 * 1000,     // 2 minutes
};

console.log('API Base URL:', PRIMARY_API_URL); // Debug: shows configured URL

class ApiService {
  constructor() {
    this.primaryApiUrl = PRIMARY_API_URL;
    this.fallbackApiUrl = FALLBACK_API_URL;
    this.fallbackActivated = false;

    this.client = axios.create({
      baseURL: this.primaryApiUrl,
      timeout: 30000,
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      async (error) => {
        const requestConfig = error?.config || {};
        const status = error?.response?.status;
        const suppressAuthLogs = requestConfig?.meta?.suppressAuthLogs === true;
        const suppressNetworkLogs = requestConfig?.meta?.suppressNetworkLogs === true;
        const isNetworkError = !error?.response && (
          error?.code === 'ERR_NETWORK' ||
          error?.code === 'ECONNREFUSED' ||
          /network|refused|failed|timeout/i.test(error?.message || '')
        );

        const usingPrimary = (requestConfig.baseURL || this.client.defaults.baseURL) === this.primaryApiUrl;
        const canFallback =
          isNetworkError &&
          !requestConfig.__fallbackRetried &&
          this.fallbackApiUrl &&
          this.fallbackApiUrl !== this.primaryApiUrl &&
          usingPrimary;

        const canRetryTransient =
          isNetworkError &&
          !requestConfig.__networkRetried;

        if (canRetryTransient) {
          requestConfig.__networkRetried = true;
          return this.client(requestConfig);
        }

        if (canFallback) {
          this.client.defaults.baseURL = this.fallbackApiUrl;
          requestConfig.baseURL = this.fallbackApiUrl;
          requestConfig.__fallbackRetried = true;

          if (!this.fallbackActivated) {
            this.fallbackActivated = true;
            console.warn('⚠️ Primary API unreachable. Switched to fallback API:', this.fallbackApiUrl);
          }

          return this.client(requestConfig);
        }

        const canRetryAuth =
          status === 401 &&
          !requestConfig.__authRetried &&
          !!auth.currentUser;

        if (canRetryAuth) {
          try {
            const refreshedToken = await auth.currentUser.getIdToken(true);
            this.setAuthToken(refreshedToken);
            requestConfig.__authRetried = true;
            requestConfig.headers = {
              ...(requestConfig.headers || {}),
              Authorization: `Bearer ${refreshedToken}`,
            };
            return this.client(requestConfig);
          } catch {
            // Fall through to normal error handling.
          }
        }

        if (error.response?.status === 404) {
          console.error('❌ Backend endpoint not found (404). Check:');
          console.error('   - Backend server is running');
          console.error('   - REACT_APP_BACKEND_URL in .env is correct');
        } else if (!suppressNetworkLogs && (error.code === 'ECONNREFUSED' || isNetworkError)) {
          console.warn('⚠️ Network request failed. Using fallback/cached data when available.');
        }

        if (!suppressAuthLogs && (status === 401 || status === 403)) {
          // Keep auth failures visible; these are not connectivity issues.
          console.warn('⚠️ API auth failure:', status);
        }

        return Promise.reject(error);
      }
    );
  }

  getApiBaseUrl() {
    return this.client.defaults.baseURL;
  }

  setAuthToken(token) {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.common['Authorization'];
    }
  }

  // User endpoints
  async getUserProfile() {
    try {
      const response = await this.client.get('/user/me');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error.message);
      throw error;
    }
  }

  async togglePremiumTestMode() {
    try {
      const response = await this.client.patch('/user/premium-test-mode');
      return response.data;
    } catch (error) {
      console.error('Error toggling premium test mode:', error.message);
      throw error;
    }
  }

  async completeOnboarding(data) {
    try {
      const response = await this.client.post('/user/onboarding', data);
      return response.data;
    } catch (error) {
      console.error('Error completing onboarding:', error.message);
      throw error;
    }
  }

  async acceptPrivacyPolicy() {
    try {
      const response = await this.client.post('/user/privacy-consent');
      return response.data;
    } catch (error) {
      console.error('Error accepting privacy policy:', error.message);
      throw error;
    }
  }

  async deleteAccount() {
    try {
      const response = await this.client.delete('/user/me');
      cache.remove('today_routines');
      cache.remove('routine_logs_50');
      cache.remove('routine_logs_100');
      cache.remove('routine_logs_500');
      cache.remove('user_streak');
      cache.remove('adherence_stats');
      cache.remove('photos_10');
      cache.remove('photos_50');
      cache.remove('photos_500');
      return response.data;
    } catch (error) {
      console.error('Error deleting account:', error.message);
      throw error;
    }
  }

  async startFreeTrial() {
    try {
      const response = await this.client.post('/user/free-trial/start');
      return response.data;
    } catch (error) {
      console.error('Error starting free trial:', error.message);
      throw error;
    }
  }

  async createRazorpayOrder(planType) {
    try {
      const response = await this.client.post('/payments/razorpay/order', {
        plan_type: planType,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating Razorpay order:', error.message);
      throw error;
    }
  }

  async verifyRazorpayPayment(payload) {
    try {
      const response = await this.client.post('/payments/razorpay/verify', payload);
      return response.data;
    } catch (error) {
      console.error('Error verifying Razorpay payment:', error.message);
      throw error;
    }
  }

  // Routine endpoints
  async createRoutineLog(routineData) {
    try {
      const response = await this.client.post('/routines', routineData);
      // Invalidate routine & streak caches after logging
      cache.remove('today_routines');
      cache.remove('routine_logs_50');
      cache.remove('routine_logs_100');
      cache.remove('routine_logs_500');
      cache.remove('user_streak');
      cache.remove('adherence_stats');
      return response.data;
    } catch (error) {
      console.error('Error creating routine log:', error.message);
      throw error;
    }
  }

  async getRoutineLogs(limit = 50, options = {}) {
    const cacheKey = `routine_logs_${limit}`;
    return cache.getOrFetch(
      cacheKey,
      async () => {
        const response = await this.client.get(`/routines?limit=${limit}`, {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        return response.data;
      },
      CACHE_TTL.ROUTINES,
      { persistent: true, ...options }
    );
  }

  async getTodayRoutines(options = {}) {
    const cacheKey = 'today_routines';
    return cache.getOrFetch(
      cacheKey,
      async () => {
        console.log('[TODAY ROUTINES] → GET /routines/today');
        const response = await this.client.get('/routines/today', {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        console.log('[TODAY ROUTINES] ✅ Raw response:', response.data);
        return response.data;
      },
      CACHE_TTL.ROUTINES,
      { persistent: false, ...options } // Don't persist today routines - changes frequently
    );
  }

  async deleteRoutineLog(logId) {
    try {
      const response = await this.client.delete(`/routines/${encodeURIComponent(logId)}`);
      cache.remove('today_routines');
      cache.remove('routine_logs_50');
      cache.remove('routine_logs_100');
      cache.remove('routine_logs_500');
      cache.remove('user_streak');
      cache.remove('adherence_stats');
      return response.data;
    } catch (error) {
      console.error('Error deleting routine log:', error.message);
      throw error;
    }
  }

  // Protocol template endpoints (with caching)
  async getProtocolTemplate(options = {}) {
    const cacheKey = 'protocol_template';
    return cache.getOrFetch(
      cacheKey,
      async () => {
        const response = await this.client.get('/protocol/template', {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        return response.data;
      },
      CACHE_TTL.PROTOCOL,
      { persistent: true, staleWhileRevalidate: true, ...options }
    );
  }

  async saveProtocolTemplate(protocolItems) {
    try {
      const response = await this.client.post('/protocol/template', protocolItems);
      // Invalidate cache after saving
      cache.remove('protocol_template');
      return response.data;
    } catch (error) {
      console.error('Error saving protocol template:', error.message);
      throw error;
    }
  }

  // Streak endpoints (with caching)
  async getStreak(options = {}) {
    const cacheKey = 'user_streak';
    return cache.getOrFetch(
      cacheKey,
      async () => {
        const response = await this.client.get('/streak', {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        return response.data;
      },
      CACHE_TTL.STREAK,
      { persistent: true, staleWhileRevalidate: true, ...options }
    );
  }

  // Photo endpoints (with caching)
  async uploadPhoto(file, photoType, notes) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('photo_type', photoType);
      if (notes) formData.append('notes', notes);

      const response = await this.client.post('/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Invalidate photos cache after upload
      cache.remove('photos_10');
      cache.remove('photos_50');
      cache.remove('photos_500');
      return response.data;
    } catch (error) {
      console.error('Error uploading photo:', error.message);
      throw error;
    }
  }

  async getPhotos(limit = 50, options = {}) {
    const cacheKey = `photos_${limit}`;
    return cache.getOrFetch(
      cacheKey,
      async () => {
        const response = await this.client.get(`/photos?limit=${limit}`, {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        return response.data;
      },
      CACHE_TTL.PHOTOS,
      { persistent: true, staleWhileRevalidate: true, ...options }
    );
  }

  async deletePhoto(photoId) {
    try {
      const response = await this.client.delete(`/photos/${photoId}`);
      // Invalidate photos cache after delete
      cache.remove('photos_10');
      cache.remove('photos_50');
      cache.remove('photos_500');
      return response.data;
    } catch (error) {
      console.error('Error deleting photo:', error.message);
      throw error;
    }
  }

  // Shedding endpoints
  async createSheddingLog(sheddingData) {
    try {
      const response = await this.client.post('/shedding', sheddingData);
      return response.data;
    } catch (error) {
      console.error('Error creating shedding log:', error.message);
      throw error;
    }
  }

  async getSheddingLogs(limit = 50) {
    try {
      const response = await this.client.get(`/shedding?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching shedding logs:', error.message);
      throw error;
    }
  }

  // Analytics endpoints (with caching)
  async getAdherenceStats(options = {}) {
    const cacheKey = 'adherence_stats';
    return cache.getOrFetch(
      cacheKey,
      async () => {
        const response = await this.client.get('/analytics/adherence', {
          meta: {
            suppressAuthLogs: options.silentAuthLogs === true,
            suppressNetworkLogs: options.silentNetworkLogs === true,
          },
        });
        return response.data;
      },
      CACHE_TTL.ADHERENCE,
      { persistent: true, staleWhileRevalidate: true, ...options }
    );
  }

  // AI endpoints (Premium)
  async sendChatMessage(message, sessionId = null) {
    try {
      const response = await this.client.post('/ai/chat', { 
        message, 
        session_id: sessionId 
      });
      return response.data;
    } catch (error) {
      console.error('Error sending chat message:', error.message);
      throw error;
    }
  }

  async getChatSessions() {
    try {
      const response = await this.client.get('/ai/chat/sessions');
      return response.data;
    } catch (error) {
      console.error('Error fetching chat sessions:', error.message);
      return [];
    }
  }

  async getChatSession(sessionId) {
    try {
      const response = await this.client.get(`/ai/chat/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat session:', error.message);
      return null;
    }
  }

  async comparePhotos(photo1Id, photo2Id) {
    try {
      const response = await this.client.post('/ai/photo-comparison', {
        photo1_id: photo1Id,
        photo2_id: photo2Id
      });
      return response.data;
    } catch (error) {
      console.error('Error comparing photos:', error.message);
      throw error;
    }
  }

  async generateWeeklyReport() {
    try {
      const response = await this.client.post('/ai/weekly-report');
      return response.data;
    } catch (error) {
      console.error('Error generating weekly report:', error.message);
      throw error;
    }
  }
  async generateMonthlyReport() {
    try {
      const response = await this.client.post('/ai/monthly-report');
      return response.data;
    } catch (error) {
      console.error('Error generating monthly report:', error.message);
      throw error;
    }
  }
  async getAIReports(limit = 10) {
    try {
      const response = await this.client.get(`/ai/reports?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching AI reports:', error.message);
      throw error;
    }
  }

  async analyzeProgress() {
    try {
      const response = await this.client.post('/ai/progress-analysis');
      return response.data;
    } catch (error) {
      console.error('Error analyzing progress:', error.message);
      throw error;
    }
  }

  async getSavedReels() {
    try {
      const response = await this.client.get('/ai/reels');
      return response.data;   // { front: {...}, back: {...}, all: {...} }
    } catch (error) {
      console.error('Error fetching saved reels:', error.message);
      return {};
    }
  }

  async generateGrowthReel(reelType = 'all', photoIds = null) {
    try {
      const body = { reel_type: reelType };
      if (photoIds && photoIds.length > 0) body.photo_ids = photoIds;
      const response = await this.client.post('/ai/generate-reel', body);
      return response.data;
    } catch (error) {
      console.error('Error generating growth reel:', error.message);
      throw error;
    }
  }

  async generateFutureSelf(file, qGoal, qFocusArea, qLengthPref) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('q_goal', qGoal);
      formData.append('q_focus_area', qFocusArea);
      formData.append('q_length_pref', qLengthPref);
      const response = await this.client.post('/ai/future-self', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 min – image generation can be slow
      });
      return response.data;
    } catch (error) {
      console.error('Error generating future self:', error.message);
      throw error;
    }
  }
}

export default new ApiService();